"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createSso = exports.SSO = void 0;
/* eslint-disable no-unmodified-loop-condition */
const js_cookie_1 = __importDefault(require("js-cookie"));
const sso_1 = require("./shared/api/sso");
const sso_2 = require("./shared/constants/sso");
const cookie_1 = require("./shared/constants/cookie");
const delay = (time) => new Promise((resolve) => {
    setTimeout(() => resolve(), time);
});
let iframeIsLoaded = false;
class SSO {
    constructor(params) {
        this.callbacks = {
            login: undefined,
            loginfailure: undefined,
            logout: undefined
        };
        this.accountUrl = SSO.getAccountUrl(params.env);
        this.loginUrl = SSO.getLoginUrl(params.env);
        this.cookieIdentifier = SSO.getCookieIdentifier(params.env);
        this.ssoSdk = params.ssoSdk;
        this.ssoUrl = this.ssoSdk.getSsoUrl();
        this.createSsoIframe();
    }
    addEventListener(event, cb) {
        this.callbacks[event] = cb;
    }
    async getToken() {
        await this.waitIframeToLoad();
        const iframe = this.getSsoIframe();
        return new Promise(resolve => {
            const validateAndReturnToken = async ({ data }) => {
                if (data.action === sso_2.AuthActions.POST_TOKEN) {
                    window.removeEventListener('message', validateAndReturnToken);
                    if (data.payload && await this.ssoSdk.tokenIsValid(data.payload)) {
                        resolve(data.payload);
                    }
                    resolve(null);
                }
            };
            window.addEventListener('message', validateAndReturnToken);
            iframe.contentWindow.postMessage({ action: sso_2.AuthActions.GET_TOKEN }, '*');
        });
    }
    async setToken(token) {
        await this.waitIframeToLoad();
        const iframe = this.getSsoIframe();
        const tokenIsValid = await this.ssoSdk.tokenIsValid(token);
        if (!tokenIsValid) {
            throw new Error('InvalidTokenError');
        }
        iframe.contentWindow.postMessage({
            action: sso_2.AuthActions.SET_TOKEN,
            payload: token
        }, '*');
    }
    async login(options) {
        const sessionToken = await this.getToken();
        if (sessionToken) {
            return this.callbacks.login(sessionToken);
        }
        if (options) {
            return this.authenticateOnPremise(options);
        }
        return this.authenticateUsingAccount();
    }
    async logout() {
        const iframe = this.getSsoIframe();
        const token = await this.getToken();
        await this.ssoSdk.logout(token);
        js_cookie_1.default.remove(this.cookieIdentifier);
        iframe.contentWindow.postMessage({
            action: sso_2.AuthActions.LOGOUT
        }, '*');
        this.callbacks.logout();
    }
    loginWithGoogle(redirectUrl) {
        window.open(`${this.ssoUrl}/auth/google?redirectTo=${redirectUrl}`, '_self');
    }
    authenticateUsingAccount() {
        const accountWindow = this.redirectToAccount();
        if (!accountWindow) {
            const error = new Error('PopupBlockedError');
            return this.callbacks.loginfailure(error);
        }
        const timer = setInterval(() => {
            if (accountWindow === null || accountWindow === void 0 ? void 0 : accountWindow.closed) {
                // get token from sso
                this.getToken()
                    .then(token => {
                    if (token) {
                        js_cookie_1.default.set(this.cookieIdentifier, token, {
                            sameSite: 'none',
                            secure: true
                        });
                        this.callbacks.login(token);
                    }
                    clearInterval(timer);
                });
            }
        }, 500);
    }
    authenticateOnPremise(options) {
        this.listenToIframeMessage();
        const iframe = this.getSsoIframe();
        iframe.contentWindow.postMessage({
            action: sso_2.AuthActions.LOGIN,
            payload: options
        }, '*');
    }
    listenToIframeMessage() {
        const authenticate = async ({ data, origin }) => {
            window.removeEventListener('message', authenticate);
            if (origin !== this.accountUrl) {
                return;
            }
            const { action, payload: token } = data;
            if (action === sso_2.AuthActions.LOGIN_SUCCESS ||
                (action === sso_2.AuthActions.POST_TOKEN && Boolean(token))) {
                js_cookie_1.default.set(this.cookieIdentifier, token, {
                    sameSite: 'none',
                    secure: true
                });
                this.callbacks.login(token);
            }
            else {
                this.callbacks.loginfailure();
            }
        };
        window.addEventListener('message', authenticate);
    }
    getSsoIframe() {
        const ssoIframe = document.getElementById('rockos-sso-account');
        return ssoIframe;
    }
    createSsoIframe() {
        const hasIframe = this.getSsoIframe();
        if (hasIframe) {
            return;
        }
        const iframe = document.createElement('iframe');
        window.addEventListener('message', ({ data }) => {
            if (data.action === sso_2.AuthActions.LOADED) {
                iframeIsLoaded = true;
            }
        });
        iframe.id = 'rockos-sso-account';
        iframe.src = this.accountUrl + '/login';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
    }
    redirectToAccount() {
        const origin = location.href;
        return window.open(`${this.loginUrl}/?origin=${origin}&sso=true`, '_self');
    }
    static getCookieIdentifier(env) {
        const identifiers = new Map()
            .set('prd', cookie_1.CookieIdentifier.PRD)
            .set('hmg', cookie_1.CookieIdentifier.HMG)
            .set('dev', cookie_1.CookieIdentifier.DEV);
        return identifiers.get(env);
    }
    static getAccountUrl(env) {
        const envs = new Map()
            .set('prd', sso_2.AccountURL.PRD)
            .set('hmg', sso_2.AccountURL.HMG)
            .set('dev', sso_2.AccountURL.DEV);
        return envs.get(env);
    }
    static getLoginUrl(env) {
        const envs = new Map()
            .set('prd', sso_2.LoginURL.PRD)
            .set('hmg', sso_2.LoginURL.HMG)
            .set('dev', sso_2.LoginURL.DEV);
        return envs.get(env);
    }
    async waitIframeToLoad() {
        const startedAt = Date.now();
        while (!iframeIsLoaded) {
            if (Date.now() - startedAt > 30000) {
                throw new Error('SSO was not loaded');
            }
            await delay(100);
        }
    }
}
exports.SSO = SSO;
function createSso(env, url) {
    const ssoSdk = new sso_1.SsoSDK({ env, url });
    return new SSO({
        env,
        ssoSdk
    });
}
exports.createSso = createSso;
//# sourceMappingURL=sso.js.map